import { socket } from '../..';
import { store } from '@shared/redux';
import {
  dialogsListApi,
  generatedApi,
  type ClientMessageData,
  type PaginatedListClientMessageData,
} from '@shared/api';
import {
  getCachedChannelOrganizationIds,
  getCachedChannelType,
  getCachedDialogFromList,
  getCachedDialogOrganizations,
  shouldUpdateDialogListByChannel,
} from './cache-scope';

export const handleChatMessage = () => {
  return socket.onMessage('CHAT_MESSAGE', async (payload) => {
    const channelId = payload.channel_id;
    const clientId = payload.client_id;
    const clientName = payload.client_name;
    const createdAt = payload.created_at;
    const creatorType = payload.creator_type;
    const message = payload.message;
    const messageId = payload.message_id;
    const managerDelta = payload.manager_delta;
    const clientAvatarUrl = payload.client_avatar_url;
    const switchedAgentId = payload.switched_agent_id;
    const isFormShown = payload.is_form_shown;
    const organizationId =
      typeof payload.organization_id === 'number' && Number.isFinite(payload.organization_id)
        ? payload.organization_id
        : null;
    const state = store.getState();
    const cachedChannelType = getCachedChannelType(state, channelId);

    const cachedGetDialogHistoryDialogHistoryGetArgs = generatedApi.util.selectCachedArgsForQuery(
      state,
      'getDialogHistoryDialogHistoryGet',
    );

    const cachedGetListDialogsDialogListPostArgs = dialogsListApi.util.selectCachedArgsForQuery(
      state,
      'getListDialogsDialogListPostQuery',
    );
    const dialogByOrganizationId = getCachedDialogOrganizations(state, clientId);
    const dialogOrganizationIds = new Set(dialogByOrganizationId.keys());
    const channelOrganizationIds = getCachedChannelOrganizationIds(state, channelId);
    if (organizationId) {
      channelOrganizationIds.add(organizationId);
    }

    cachedGetDialogHistoryDialogHistoryGetArgs?.forEach((args) => {
      if (args.clientId !== clientId) return;
      if ((args.offset ?? 0) > 0) return;

      store.dispatch(
        generatedApi.util.updateQueryData('getDialogHistoryDialogHistoryGet', args, (draft) => {
          draft.items = draft.items ?? [];

          const nextMessage = {
            id: messageId,
            creator_type: creatorType,
            message,
            created_at: createdAt,
            manager_delta: managerDelta,
            switched_agent_id: switchedAgentId ?? null,
            is_form_shown: isFormShown,
          };

          const exists = draft.items?.some((item) => item.id === messageId);

          if (exists) return;

          const merged = [...draft.items, nextMessage].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
          );

          draft.items = merged;

          draft.count =
            draft.count !== undefined && draft.count !== null
              ? draft.count + 1
              : draft.items.length;
        }),
      );
    });

    const hasCachedFirstPage = cachedGetDialogHistoryDialogHistoryGetArgs?.some(
      (args) => args.clientId === clientId && (args.offset ?? 0) === 0,
    );

    if (!hasCachedFirstPage) {
      const nextMessage: ClientMessageData = {
        id: messageId,
        creator_type: creatorType,
        message,
        created_at: createdAt,
        manager_delta: managerDelta,
        switched_agent_id: switchedAgentId ?? null,
        is_form_shown: isFormShown,
      };
      const initial: PaginatedListClientMessageData = {
        items: [nextMessage],
        count: 1,
        offset: 0,
        limit: 20,
      };

      store.dispatch(
        generatedApi.util.upsertQueryData(
          'getDialogHistoryDialogHistoryGet',
          { clientId, offset: 0, limit: 20 },
          initial,
        ),
      );
    }

    cachedGetListDialogsDialogListPostArgs?.forEach((args) => {
      const filterLead = args?.dialogListFilters?.is_lead === true;
      const hasSearch = Boolean((args?.dialogListFilters as { q?: string } | undefined)?.q);
      const cachedDialog = getCachedDialogFromList(state, args, clientId);

      if (
        !shouldUpdateDialogListByChannel({
          args,
          channelId,
          hasExistingDialog: Boolean(cachedDialog),
          dialogOrganizationIds,
          channelOrganizationIds,
          eventOrganizationId: organizationId,
        })
      ) {
        return;
      }

      store.dispatch(
        dialogsListApi.util.updateQueryData('getListDialogsDialogListPostQuery', args, (draft) => {
          if (!draft.items) return;

          const currentIndex = draft.items.findIndex((dialog) => dialog.client_id === clientId);
          const currentDialog = currentIndex >= 0 ? draft.items[currentIndex] : null;
          const channelDialog = draft.items.find((dialog) => dialog.channel_id === channelId);
          const listOrganizationId =
            typeof args.dialogListFilters?.organization_id === 'number'
              ? args.dialogListFilters.organization_id
              : null;
          const organizationDialog = listOrganizationId
            ? (dialogByOrganizationId.get(listOrganizationId) ?? null)
            : null;
          const currentLeadCreatedAt =
            currentDialog?.lead_created_at ?? organizationDialog?.lead_created_at ?? null;

          if (filterLead && !currentLeadCreatedAt) return;
          if (hasSearch && !currentDialog) return;

          const resolvedChannelType =
            currentDialog?.channel_type ??
            organizationDialog?.channel_type ??
            channelDialog?.channel_type ??
            cachedChannelType ??
            'widget';
          const shouldMarkOnline =
            !currentDialog || (resolvedChannelType === 'widget' && creatorType === 'USER');

          const updatedDialog = {
            ...(currentDialog ??
              organizationDialog ?? {
                client_id: clientId,
                client_name: clientName ?? null,
                channel_type: resolvedChannelType,
                channel_id: channelId ?? 0,
                client_avatar_url: clientAvatarUrl ?? null,
                lead_created_at: currentLeadCreatedAt,
                client_uuid: channelDialog?.client_uuid ?? '',
                channel_value: '',
                is_test: channelDialog?.is_test ?? false,
              }),
            last_message: message,
            last_message_at: createdAt,
            client_name:
              clientName ?? currentDialog?.client_name ?? organizationDialog?.client_name ?? null,
            channel_id:
              channelId ?? currentDialog?.channel_id ?? organizationDialog?.channel_id ?? 0,
            channel_type: resolvedChannelType,
            client_avatar_url:
              clientAvatarUrl ??
              currentDialog?.client_avatar_url ??
              organizationDialog?.client_avatar_url ??
              null,
            lead_created_at: currentLeadCreatedAt,
            channel_value:
              currentDialog?.channel_value ??
              organizationDialog?.channel_value ??
              channelDialog?.channel_value ??
              '',
            is_online: shouldMarkOnline
              ? true
              : (currentDialog?.is_online ?? organizationDialog?.is_online ?? false),
          };

          const restDialogs =
            currentIndex >= 0
              ? draft.items.filter((_, index) => index !== currentIndex)
              : draft.items;

          draft.items = [updatedDialog, ...restDialogs].sort((a, b) =>
            b.last_message_at.localeCompare(a.last_message_at),
          );

          draft.count =
            draft.count !== undefined && draft.count !== null
              ? draft.items.length > draft.count
                ? draft.count + 1
                : draft.count
              : draft.items.length;
        }),
      );
    });
  });
};
